/**
 * Delegated outbound-click tracking.
 *
 * One capture-phase listener on `document` catches every outbound link on the
 * site, including links added later — no per-link `onClick` wiring needed.
 *
 * IMPORTANT — do NOT "clean up" the explicit `track()` calls in components.
 * Those fire distinct, hand-curated event names (`book_now_click`,
 * `service_booking_click`, `salon_select`, …) with editorial `cta_location`
 * values. This listener fires a different set of names (`booking_click`,
 * `call_click`, `directions_click`, `social_click`, `outbound_click`), so the
 * two never collide and nothing is double counted. Removing either one loses
 * data.
 *
 * Safe to call before consent: it only pushes to `dataLayer` / `gtag`, and
 * Consent Mode v2 governs whether anything is actually stored or transmitted.
 */

import { ADS_BOOKING_SEND_TO } from "./load-analytics";

const SITE_HOSTS = ["tailorsbarber.se", "www.tailorsbarber.se"];


const SOCIAL_HOSTS = ["instagram.com", "facebook.com", "tiktok.com", "youtube.com", "youtu.be"];

/** BokaDirekt URL slug fragment -> human salon name. */
const SALON_BY_SLUG: Array<[string, string]> = [
  ["taby-centrum", "Täby Centrum"],
  ["stockholm-ahlens-city", "Åhléns City"],
  ["akersberga", "Åkersberga"],
  ["arninge", "Arninge"],
];

type OutboundEvent =
  | "booking_click"
  | "call_click"
  | "directions_click"
  | "social_click"
  | "outbound_click";

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

function classify(href: string): OutboundEvent | null {
  const lower = href.toLowerCase();
  if (lower.startsWith("tel:")) return "call_click";
  if (lower.includes("bokadirekt.se")) return "booking_click";
  if (lower.includes("google.com/maps") || lower.includes("maps.app.goo.gl") || lower.includes("maps.google."))
    return "directions_click";
  if (SOCIAL_HOSTS.some((h) => lower.includes(h))) return "social_click";

  if (lower.startsWith("mailto:")) return "outbound_click";
  try {
    const url = new URL(href, location.href);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    const host = url.hostname.toLowerCase();
    if (SITE_HOSTS.includes(host) || host === "localhost" || host === "127.0.0.1") return null;
    return "outbound_click";
  } catch {
    return null;
  }
}

function linkDomain(href: string) {
  if (href.toLowerCase().startsWith("tel:")) return "phone";
  if (href.toLowerCase().startsWith("mailto:")) return "email";
  try {
    return new URL(href, location.href).hostname.toLowerCase();
  } catch {
    return "unknown";
  }
}

/**
 * cta_location resolution order:
 *   data-cta -> nearest ancestor <section> id -> nearest preceding h1/h2 text -> "unknown"
 */
function resolveCtaLocation(anchor: HTMLAnchorElement) {
  const explicit = anchor.getAttribute("data-cta");
  if (explicit) return explicit;

  const section = anchor.closest("section");
  if (section?.id) return section.id;

  const headings = Array.from(document.querySelectorAll("h1, h2"));
  let previous: Element | null = null;
  for (const heading of headings) {
    const position = heading.compareDocumentPosition(anchor);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) previous = heading;
    else break;
  }
  const text = (previous as HTMLElement | null)?.innerText?.trim();
  if (text) return slugify(text) || "unknown";

  return "unknown";
}

function resolveSalonName(anchor: HTMLAnchorElement, href: string, event: OutboundEvent) {
  const explicit = anchor.getAttribute("data-salon");
  if (explicit) return explicit;
  if (event !== "booking_click") return undefined;
  const lower = href.toLowerCase();
  const match = SALON_BY_SLUG.find(([slug]) => lower.includes(slug));
  return match?.[1];
}

/**
 * Per-service conversion value for an outbound BokaDirekt click, keyed on the
 * BokaDirekt service slug in the link URL.
 *
 * All amounts are SEK EXCLUDING VAT (list price / 1.25). Tailor's Barber
 * reports every financial figure ex VAT, so these must NOT be "corrected" back
 * to the inc-VAT list prices (450 / 380 / 450 / 680).
 */
const BOOKING_VALUE_BY_SLUG: Array<[string, number]> = [
  ["klippning-skaggtrimning-1247224", 544], // Hår & Skägg, 680 inc VAT
  ["skagg-trimning-1247227", 304], // Skäggtrimning, 380 inc VAT
  ["skinfade-taper-fade-2173161", 360], // Taper/Skin fade, 450 inc VAT
  ["klippning-1247219", 360], // Herrklippning, 450 inc VAT
];

/** Default for generic salon or chain links: standard herrklippning ex VAT. */
const BOOKING_VALUE_DEFAULT = 360;

/**
 * The 15% campaign landing page sells the exact same BokaDirekt services at a
 * reduced price, so a booking from it is genuinely worth 15% less. Reporting the
 * full price there would overstate that campaign and make Smart Bidding
 * over-invest in it relative to the full-price pages.
 */
const DISCOUNT_LANDING_PATH = "/15-rabatt";
const DISCOUNT_RATE = 0.15;

function bookingValue(href: string) {
  const lower = href.toLowerCase();
  const match = BOOKING_VALUE_BY_SLUG.find(([slug]) => lower.includes(slug));
  const base = match?.[1] ?? BOOKING_VALUE_DEFAULT;

  const pathname =
    typeof location !== "undefined" && typeof location.pathname === "string"
      ? location.pathname
      : "";
  if (!pathname.startsWith(DISCOUNT_LANDING_PATH)) return base;

  const discounted = Math.round(base * (1 - DISCOUNT_RATE));
  return Number.isFinite(discounted) ? discounted : base;
}


let initialised = false;

export function initOutboundTracking() {
  if (typeof document === "undefined" || initialised) return;
  initialised = true;

  document.addEventListener(
    "click",
    (e) => {
      const target = e.target as Element | null;
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href) return;

      const event = classify(href);
      if (!event) return;

      const params: Record<string, unknown> = {
        link_url: href,
        link_text: (anchor.innerText || anchor.textContent || "").trim().slice(0, 100),
        link_domain: linkDomain(href),
        page_path: location.pathname,
        cta_location: resolveCtaLocation(anchor),
      };

      const salonName = resolveSalonName(anchor, href, event);
      if (salonName) params.salon_name = salonName;

      window.gtag?.("event", event, params);
      window.dataLayer?.push({ event, ...params });

      // Google Ads conversion: a click through to BokaDirekt is the booking
      // action we optimise for. GA4 keeps its own `booking_click` event above;
      // this is the separate Ads-side conversion hit, so nothing is double
      // counted between the two products.
      if (event === "booking_click") {
        window.gtag?.("event", "conversion", {
          send_to: ADS_BOOKING_SEND_TO,
          value: bookingValue(href),
          currency: "SEK",
          salon_name: params.salon_name,
          cta_location: params.cta_location,
        });
      }

    },
    { capture: true, passive: true },
  );
}
